export interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

const LIGHT_SURFACE = '#ffffff';
/** WCAG AA for UI components / large text — keeps brand hue more visible than 4.5:1 body text. */
const MIN_UI_CONTRAST = 3.0;

export function normalizeHexColor(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const trimmed = input.trim().toLowerCase();
  if (trimmed === 'white') {
    return '#ffffff';
  }
  if (trimmed === 'black') {
    return '#000000';
  }
  if (/^#[0-9a-f]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed.match(/^#(.)(.)(.)$/)!;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9a-f]{6}$/.test(trimmed)) {
    return trimmed;
  }

  const rgbMatch = trimmed.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (rgbMatch) {
    return rgbToHex({
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    });
  }

  return null;
}

export function parseColor(input: string): Rgb | null {
  const hex = normalizeHexColor(input);
  if (!hex) {
    return null;
  }

  const num = parseInt(hex.slice(1), 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));
  return `#${((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1)}`;
}

export function hexToRgbString(input: string): string {
  const rgb = parseColor(input);
  if (!rgb) {
    return '0, 0, 0';
  }

  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const transform = (channel: number) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * transform(r) + 0.7152 * transform(g) + 0.0722 * transform(b);
}

export function contrastRatio(foreground: string, background: string): number {
  const fg = parseColor(foreground);
  const bg = parseColor(background);
  if (!fg || !bg) {
    return 1;
  }

  const fgLuminance = relativeLuminance(fg);
  const bgLuminance = relativeLuminance(bg);
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

export function hasReadableContrastOnWhite(color: string, minRatio = MIN_UI_CONTRAST): boolean {
  return contrastRatio(color, LIGHT_SURFACE) >= minRatio;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  const l = (max + min) / 2;
  let s = 0;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) {
      h += 360;
    }
  }

  return { h, s, l };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rn = 0;
  let gn = 0;
  let bn = 0;

  if (h < 60) {
    rn = c; gn = x;
  } else if (h < 120) {
    rn = x; gn = c;
  } else if (h < 180) {
    gn = c; bn = x;
  } else if (h < 240) {
    gn = x; bn = c;
  } else if (h < 300) {
    rn = x; bn = c;
  } else {
    rn = c; bn = x;
  }

  return {
    r: (rn + m) * 255,
    g: (gn + m) * 255,
    b: (bn + m) * 255,
  };
}

function mixHexColors(colorA: string, colorB: string, ratio: number): string {
  const a = parseColor(colorA);
  const b = parseColor(colorB);
  if (!a || !b) {
    return colorA;
  }

  const weight = Math.max(0, Math.min(1, ratio));
  return rgbToHex({
    r: a.r * (1 - weight) + b.r * weight,
    g: a.g * (1 - weight) + b.g * weight,
    b: a.b * (1 - weight) + b.b * weight,
  });
}

export function ensureContrastOnBackground(
  foreground: string,
  background: string,
  minRatio = MIN_UI_CONTRAST,
): string {
  const fg = parseColor(foreground);
  const bg = parseColor(background);
  if (!fg || !bg) {
    return foreground;
  }

  const normalized = normalizeHexColor(foreground) ?? foreground;
  if (contrastRatio(normalized, background) >= minRatio) {
    return normalized;
  }

  const bgLuminance = relativeLuminance(bg);
  if (bgLuminance <= 0.5) {
    for (let step = 0; step < 16; step++) {
      const candidate = mixHexColors(normalized, '#ffffff', (step + 1) * 0.06);
      if (contrastRatio(candidate, background) >= minRatio) {
        return candidate;
      }
    }
    return '#ffffff';
  }

  const hsl = rgbToHsl(fg);
  const saturation = Math.max(hsl.s, 0.55);

  for (let lightness = hsl.l; lightness >= 0.22; lightness -= 0.03) {
    const candidate = rgbToHex(hslToRgb({ h: hsl.h, s: saturation, l: lightness }));
    if (contrastRatio(candidate, background) >= minRatio) {
      return candidate;
    }
  }

  for (let mix = 0.2; mix <= 0.55; mix += 0.05) {
    const candidate = mixHexColors(normalized, '#1f2937', mix);
    if (contrastRatio(candidate, background) >= minRatio) {
      return candidate;
    }
  }

  return '#a16207';
}
